import React, { useContext, useState } from 'react'
import { Modal, Form, Input, Button, Typography, message, Divider } from 'antd'
import { CloseOutlined } from '@ant-design/icons'
import { AuthContext } from '../auth/AuthContext'
import { axiosForm } from '../../axiosInstances'
import { useTranslation } from 'react-i18next'
import LanguageSwitcher from '../common/LanguageSwitcher'

const { Title } = Typography

const SettingsModal = ({ open, onClose }) => {
  const { user, logout } = useContext(AuthContext)
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [deletePassword, setDeletePassword] = useState("")
  const { t } = useTranslation()

  const onFinish = async (values) => {
    setLoading(true)
    try {
      await axiosForm.post('authentication/api/change-password/', {
        old_password: values.old_password,
        new_password: values.new_password,
      })
      message.success(t('password_changed_success'))
      form.resetFields()
      onClose()
    } catch (e) {
      message.error(e.response?.data?.detail || t('password_change_error'))
    }
    setLoading(false)
  }

  const handleDelete = async () => {
    setDeleteLoading(true)
    try {
      await axiosForm.delete('authentication/api/delete-account/', {
        data: { password: deletePassword }
      })
      message.success(t('account_deleted'))
      setDeleteModalOpen(false)
      logout()
      onClose()
    } catch (e) {
      message.error(e.response?.data?.detail || t('account_delete_error'))
    }
    setDeleteLoading(false)
    setDeletePassword("")
  }

  // Custom header for the modal
  const modalHeader = (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      width: '100%',
      paddingRight: 0,
      paddingBottom: 0
    }}>
      <Title level={4} style={{ margin: 0, flex: 1 }}>
        {t('account_settings')}
      </Title>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <LanguageSwitcher />
        <Button
          type="text"
          icon={<CloseOutlined />}
          onClick={onClose}
          style={{ fontSize: 18, marginLeft: 8 }}
          aria-label="Close"
        />
      </div>
    </div>
  )

  return (
    <>
      <Modal
        open={open}
        footer={null}
        centered
        title={null}
        closable={false}
        styles={{
          body: { paddingTop: 18 },
          mask: { backdropFilter: 'blur(5px)' }
        }}
        destroyOnHidden
        onCancel={onClose} // still allow esc and background click to close
      >
        {modalHeader}
        <Divider style={{ margin: '12px 0 18px 0' }} />
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Form.Item label={t('email')}>
            <Input value={user?.email || user?.username} disabled />
          </Form.Item>
          <Divider />
          <Title level={5}>{t('change_password')}</Title>
          <Form.Item
            label={t('current_password')}
            name="old_password"
            rules={[{ required: true, message: t('enter_current_password') }]}
          >
            <Input.Password />
          </Form.Item>
          <Form.Item
            label={t('new_password')}
            name="new_password"
            rules={[{ required: true, message: t('enter_new_password') }]}
          >
            <Input.Password />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading} block>
              {t('change_password')}
            </Button>
          </Form.Item>
        </Form>
        <Divider />
        <Button danger type="primary" block onClick={() => setDeleteModalOpen(true)}>
          {t('delete_account')}
        </Button>
      </Modal>
      {/* Delete confirmation modal */}
      <Modal
        open={deleteModalOpen}
        onCancel={() => { setDeleteModalOpen(false); setDeletePassword(""); }}
        title={t('delete_confirm_title')}
        footer={null}
        centered
        styles={{
          body: { paddingTop: 18 },
          mask: { backdropFilter: 'blur(5px)' }
        }}
        destroyOnHidden
      >
        <p>
          {t('delete_confirm_desc')}
        </p>
        <Form
          layout="vertical"
          onFinish={handleDelete}
        >
          <Form.Item
            label={t('password')}
            required
            rules={[{ required: true, message: t('enter_password') }]}
          >
            <Input.Password
              value={deletePassword}
              onChange={e => setDeletePassword(e.target.value)}
              placeholder={t('password')}
            />
          </Form.Item>
          <Form.Item>
            <Button
              type="primary"
              danger
              htmlType="submit"
              loading={deleteLoading}
              disabled={!deletePassword}
              block
            >
              {t('delete_forever')}
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
}

export default SettingsModal
